import { Vehicle, type Vendor } from '@cruz-com/lookup'
import dayjs from 'dayjs'
import _ from 'lodash'
import Image from 'next/image'
import NextLink from 'next/link'
import { memo } from 'react'
import { Bullet, Divider } from '@/components'

const Price = memo<{
  price?: number
  sale_status_id?: Vehicle.SaleStatusEnum
  history?: Vehicle.Summary['history']
}>(({ price, sale_status_id, history }) => {
  if (sale_status_id === Vehicle.SaleStatusEnum.NotForSale) {
    return 'Not for Sale'
  }
  if (sale_status_id === Vehicle.SaleStatusEnum.OpenToOffers) {
    return 'Open to Offers'
  }
  let _price = price || 0
  if (_.size(history?.price) > 1) {
    _price = _.last(history?.price)?.value || price || 0
  }
  if (!price) {
    return 'Price Unknown'
  }
  return (
    <div>
      {sale_status_id === Vehicle.SaleStatusEnum.Sold && <span className='mr-1'>Sold:</span>}${_price.toLocaleString()}
    </div>
  )
})

const Mileage = memo<{ mileage?: number }>(({ mileage }) => {
  if (!mileage) {
    return null
  }
  return (
    <>
      <Bullet />
      <div>
        <span>{mileage?.toLocaleString()}</span>
        <span className='ml-1'>miles</span>
      </div>
    </>
  )
})

const VehicleSummary = memo<{ vehicle?: Vehicle.Summary | Partial<Vehicle.Detail>; url?: string }>(({ vehicle, url }) => {
  if (!_.get(vehicle, 'year')) {
    return (
      <div className='flex h-full items-center justify-center font-serif text-lg font-normal italic' role='alert'>
        This vehicle was removed by the seller or Cruz.com
      </div>
    )
  }
  const { year, make, model, trim, price, sale_status_id, condition_name, mileage, history, photo_preview_url, location } =
    (vehicle as Vehicle.Summary) || {}
  const { name, city, state_abbr, zip_code } = location as Vendor.Location
  const title = _.compact([year, make, model, trim]).join(' ')

  return (
    <NextLink hrefLang='en' href={url || ''} className='block'>
      <figure className='m-0 flex gap-x-5'>
        <div className='relative w-[120px] shrink-0 sm:w-[161px]'>
          {sale_status_id === Vehicle.SaleStatusEnum.Sold && (
            <div className='absolute right-0 bottom-0 left-0 rounded-b-sm bg-zinc-700/50 px-4 py-2 text-center text-sm font-bold tracking-wider text-white dark:bg-zinc-700/50'>
              SOLD
            </div>
          )}
          <Image
            width={480}
            height={480}
            src={photo_preview_url || '/img/noPhoto.svg'}
            alt={title}
            priority={true}
            fetchPriority='high'
            unoptimized={true}
            className={_.isEmpty(photo_preview_url) ? 'card-img-no' : 'card-img rounded'}
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.src = '/img/noPhoto.svg'
            }}
          />
        </div>
        <figcaption className='flex grow flex-col'>
          <h3 className='m-0 text-base font-semibold'>{title}</h3>
          <div className='flex items-center gap-x-2 pt-1 text-sm'>
            <Price price={price} sale_status_id={sale_status_id} history={history} />
            <Bullet />
            <div>{condition_name}</div>
            <Mileage mileage={mileage} />
          </div>
          <Divider className='py-3' />
          <address className='text-sm/5 not-italic'>
            <span className='font-medium'>{name}</span>
            <br />
            {city}, {state_abbr} {zip_code}
          </address>
        </figcaption>
      </figure>
    </NextLink>
  )
})

const InquiryDetails = memo<{
  subject?: string
  date_create?: string
  message?: string
}>(({ subject, date_create, message }) => (
  <section className='space-y-4 p-5'>
    <div className='flex flex-col gap-y-1 sm:flex-row sm:justify-between md:items-center'>
      <div className='space-x-2 text-sm'>
        <span>Subject:</span>
        <span className='font-medium'>{subject}</span>
      </div>
      <div className='space-x-2 text-sm'>
        <time dateTime={date_create}>{dayjs(date_create).fromNow()}</time>
        <time dateTime={date_create}>({dayjs(date_create).format('MMMM D, YYYY, h:mm A')})</time>
      </div>
    </div>
    <p className='text-base/7 font-normal'>{message}</p>
  </section>
))

export const InquiryCard = memo<Vehicle.Lead>((d) => (
  <article className='flex h-full flex-col divide-y'>
    <div className='grow rounded-t-sm bg-zinc-100/80 p-5 dark:bg-zinc-800'>
      <VehicleSummary vehicle={d.vehicle} url={d?.vehicle?.url} />
    </div>
    <InquiryDetails subject={d.subject} date_create={d.date_create} message={d.message} />
  </article>
))
